import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Badge, 
  Spinner,
  OverlayTrigger,
  Tooltip,
  InputGroup
} from 'react-bootstrap';
import { doc, getDoc, setDoc, getFirestore } from 'firebase/firestore';
import { 
  FaCog, 
  FaSave, 
  FaInfoCircle,
  FaPercent,
  FaDollarSign,
  FaShieldAlt,
  FaUserShield,
  FaChartLine,
  FaGraduationCap,
  FaExclamationTriangle,
  FaCheck
} from 'react-icons/fa';
import { BsGear, BsShield, BsCalculator } from 'react-icons/bs';
import Swal from 'sweetalert2';
import firebaseApp from '../../firebase/credenciales';

const db = getFirestore(firebaseApp);

const ConfiguracionPlanilla = ({ usuario }) => {
    const [config, setConfig] = useState({
        // Variables Globales
        uit: 5500,
        sueldo_minimo: 1130,
        ruc_empresa: usuario.ruc,
        asignacion_familiar: 113.00,
        essalud_vida: 3.00,
        created_at: null,
        updated_at: null,
        porcentaje_essalud: 0.09,
        porcentaje_onp: 0.13,
        // AFP Integra
        afp_integra: {
            fondo: 0.10,
            seguro: 0.0137,
            comision: 0.0155
        },
        // AFP Prima
        afp_prima: {
            fondo: 0.10,
            seguro: 0.0137,
            comision: 0.0160
        },
        // AFP Profuturo
        afp_profuturo: {
            fondo: 0.10,
            seguro: 0.0137,
            comision: 0.0169
        },
        // AFP Habitat
        afp_habitat: {
            fondo: 0.10,
            seguro: 0.0137,
            comision: 0.0147
        },
        // SCTR por nivel de riesgo
        sctr: {
            riesgo_1: {
                salud: 0.0063,
                pension: 0.0060
            },
            riesgo_2: {
                salud: 0.0123,
                pension: 0.0120
            },
            riesgo_3: {
                salud: 0.0183,
                pension: 0.0180
            },
            riesgo_4: {
                salud: 0.0243,
                pension: 0.0240
            },
            riesgo_5: {
                salud: 0.0303,
                pension: 0.0300
            }
        },
        // Vida Ley
        vida_ley: {
            rango_1: { // Hasta 100K
                tasa: 0.0053
            },
            rango_2: { // De 100K a 200K
                tasa: 0.0042
            },
            rango_3: { // Más de 200K
                tasa: 0.0031
            }
        },
        // SENATI
        senati: {
            tasa: 0.0075 // 0.75% de la remuneración
        },
        // Otros
        dias_trabajo_mes: 30,
        horas_trabajo_dia: 8,
        ruc_empresa: usuario.ruc,
        tipo_seguro_micro: 'ESSALUD', // ESSALUD o SIS
        costo_sis: 15.00
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    useEffect(() => {
        cargarConfiguracion();
    }, []);

    const cargarConfiguracion = async () => {
        try {
            const configRef = doc(db, "pl_config", usuario.ruc);
            const docSnap = await getDoc(configRef);
    
            if (docSnap.exists()) {
                const data = docSnap.data();
                // Convertir timestamps a Date si existen
                if (data.created_at) {
                    data.created_at = data.created_at.toDate();
                }
                if (data.updated_at) {
                    data.updated_at = data.updated_at.toDate();
                    setLastSaved(data.updated_at);
                }
                
                setConfig(prevConfig => ({
                    ...prevConfig,  // Mantener valores por defecto
                    ...data        // Sobrescribir con datos de Firestore
                }));
            }
        } catch (error) {
            console.error("Error al cargar configuración:", error);
            mostrarError('Error al cargar la configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        if (name.includes('afp_')) {
            const [afp, campo] = name.split('.');
            setConfig(prev => ({
                ...prev,
                [afp]: {
                    ...prev[afp],
                    [campo]: parseFloat(value)
                }
            }));
        } else if (name.includes('sctr.')) {
            const [_, riesgo, campo] = name.split('.');
            setConfig(prev => ({
                ...prev,
                sctr: {
                    ...prev.sctr,
                    [riesgo]: {
                        ...prev.sctr[riesgo],
                        [campo]: parseFloat(value)
                    }
                }
            }));
        } else if (name.includes('vida_ley.')) {
            const [_, rango, campo] = name.split('.');
            setConfig(prev => ({
                ...prev,
                vida_ley: {
                    ...prev.vida_ley,
                    [rango]: {
                        ...prev.vida_ley[rango],
                        [campo]: parseFloat(value)
                    }
                }
            }));
        } else if (name.includes('senati.')) {
            const [_, campo] = name.split('.');
            setConfig(prev => ({
                ...prev,
                senati: {
                    ...prev.senati,
                    [campo]: parseFloat(value)
                }
            }));
        } else {
            setConfig(prev => ({
                ...prev,
                [name]: type === 'number' ? parseFloat(value) : value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones exhaustivas antes de guardar
        const errores = validarConfiguracion(config);

        if (errores.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Errores de Validación',
                html: `<div style="text-align: left;"><strong>Por favor corrija los siguientes errores:</strong><br>• ${errores.join('<br>• ')}</div>`,
                confirmButtonText: 'Entendido'
            });
            return;
        }

        setSaving(true);

        try {
            const configRef = doc(db, "pl_config", usuario.ruc);
            const dataToSave = {
                ...config,
                ruc_empresa: usuario.ruc,
                updated_at: new Date(),
                created_at: config.created_at || new Date()
            };

            await setDoc(configRef, dataToSave);
            setLastSaved(new Date());

            Swal.fire({
                icon: 'success',
                title: 'Configuración guardada',
                text: 'Los cambios se guardaron correctamente',
                confirmButtonColor: '#8B4513',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error("Error al guardar:", error);
            mostrarError('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    // Función de validación de configuración
    const validarConfiguracion = (configuracion) => {
        const errores = [];

        // Validar UIT
        if (!configuracion.uit || typeof configuracion.uit !== 'number' || configuracion.uit <= 0) {
            errores.push('UIT debe ser un número positivo');
        }

        // Validar sueldo mínimo
        if (!configuracion.sueldo_minimo || typeof configuracion.sueldo_minimo !== 'number' || configuracion.sueldo_minimo <= 0) {
            errores.push('Sueldo mínimo debe ser un número positivo');
        }

        // Validar asignación familiar
        if (!configuracion.asignacion_familiar || typeof configuracion.asignacion_familiar !== 'number' || configuracion.asignacion_familiar < 0) {
            errores.push('Asignación familiar debe ser un número no negativo');
        }

        // Validar porcentajes AFP
        if (configuracion.afp_integra) {
            const afp = configuracion.afp_integra;
            if (typeof afp.fondo !== 'number' || afp.fondo <= 0 || afp.fondo > 1) {
                errores.push('Fondo AFP Integra debe ser un porcentaje válido (0-100%)');
            }
            if (typeof afp.seguro !== 'number' || afp.seguro <= 0 || afp.seguro > 1) {
                errores.push('Seguro AFP Integra debe ser un porcentaje válido (0-100%)');
            }
            if (typeof afp.comision !== 'number' || afp.comision <= 0 || afp.comision > 1) {
                errores.push('Comisión AFP Integra debe ser un porcentaje válido (0-100%)');
            }
        }

        // Validar porcentajes ESSALUD
        if (typeof configuracion.porcentaje_essalud !== 'number' || configuracion.porcentaje_essalud <= 0 || configuracion.porcentaje_essalud > 1) {
            errores.push('Porcentaje ESSALUD debe ser un porcentaje válido (0-100%)');
        }

        // Validar porcentaje ONP
        if (typeof configuracion.porcentaje_onp !== 'number' || configuracion.porcentaje_onp <= 0 || configuracion.porcentaje_onp > 1) {
            errores.push('Porcentaje ONP debe ser un porcentaje válido (0-100%)');
        }

        // Validar costo SIS
        if (configuracion.tipo_seguro_micro === 'SIS' && (!configuracion.costo_sis || typeof configuracion.costo_sis !== 'number' || configuracion.costo_sis < 0)) {
            errores.push('El costo del SIS debe ser un número no negativo');
        }

        // Validar SCTR por niveles de riesgo
        if (configuracion.sctr) {
            for (let nivel = 1; nivel <= 5; nivel++) {
                const riesgo = configuracion.sctr[`riesgo_${nivel}`];
                if (riesgo) {
                    if (typeof riesgo.salud !== 'number' || riesgo.salud <= 0 || riesgo.salud > 1) {
                        errores.push(`SCTR Riesgo ${nivel} - Salud debe ser un porcentaje válido (0-100%)`);
                    }
                    if (typeof riesgo.pension !== 'number' || riesgo.pension <= 0 || riesgo.pension > 1) {
                        errores.push(`SCTR Riesgo ${nivel} - Pensión debe ser un porcentaje válido (0-100%)`);
                    }
                }
            }
        }

        return errores;
    };

    const mostrarError = (mensaje) => {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: mensaje,
            confirmButtonColor: '#8B4513'
        });
    };

    if (loading) {
        return (
            <div
                className="min-vh-100 d-flex justify-content-center align-items-center"
                style={{
                    background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                }}
            >
                <div className="text-center">
                    <Spinner
                        animation="border"
                        style={{ color: "#8B4513", width: "3rem", height: "3rem" }}
                    />
                    <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
                        Cargando configuración...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {/* Marco principal */}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium */}
                    <Card.Header
                        className="text-white py-4"
                        style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            borderBottom: "3px solid #D2691E",
                        }}
                    >
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <BsGear className="me-3" />
                                Configuración de Planillas
                            </h2>
                            <p className="mb-0 opacity-75">
                                Configure los parámetros y tasas para el cálculo de planillas
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Barra superior con información */}
                        <Card
                            className="shadow-sm border-0 mb-4"
                            style={{ borderRadius: "15px" }}
                        >
                            <Card.Body className="p-4">
                                <Row className="align-items-center">
                                    <Col md={1}>
                                        <div
                                            className="rounded-3 p-3 text-center"
                                            style={{
                                                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                                color: "white",
                                            }}
                                        >
                                            <FaCog size={24} />
                                        </div>
                                    </Col>
                                    <Col md={7}>
                                        <h5 className="mb-1 fw-semibold">
                                            Parámetros de Configuración
                                        </h5>
                                        <p className="text-muted mb-0 small">
                                            Gestiona las tasas y valores utilizados en el cálculo de las planillas
                                        </p>
                                    </Col>
                                    <Col md={4} className="text-end">
                                        <div className="d-flex gap-2 justify-content-end flex-wrap">
                                            {lastSaved && (
                                                <Badge
                                                    style={{
                                                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                                                        fontSize: "0.8rem",
                                                        padding: "6px 12px"
                                                    }}
                                                >
                                                    <FaCheck className="me-1" />
                                                    Última actualización: {lastSaved.toLocaleString()}
                                                </Badge>
                                            )}
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        Estos valores se utilizan para calcular las planillas.
                                                        Manténgalos actualizados según la normativa vigente.
                                                    </Tooltip>
                                                }
                                            >
                                                <Badge
                                                    style={{
                                                        background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                                                        fontSize: "0.8rem",
                                                        padding: "6px 12px",
                                                        cursor: "help"
                                                    }}
                                                >
                                                    <FaExclamationTriangle className="me-1" />
                                                    Información importante
                                                </Badge>
                                            </OverlayTrigger>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Form onSubmit={handleSubmit}>
                            {/* Variables Globales */}
                            <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="text-white py-3"
                                    style={{
                                        background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                                        borderRadius: "15px 15px 0 0",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <BsCalculator className="me-2" />
                                        <h5 className="mb-0 fw-semibold">Variables Globales</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-3">
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaDollarSign className="me-1" />
                                                    UIT
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        S/
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="uit"
                                                        value={config.uit}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "0 10px 10px 0" }}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Unidad Impositiva Tributaria
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaDollarSign className="me-1" />
                                                    Sueldo Mínimo
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        S/
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="sueldo_minimo"
                                                        value={config.sueldo_minimo}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "0 10px 10px 0" }}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Remuneración Mínima Vital
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaPercent className="me-1" />
                                                    ESSALUD
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="porcentaje_essalud"
                                                        value={config.porcentaje_essalud}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Contribución del empleador
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaPercent className="me-1" />
                                                    ONP
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="porcentaje_onp"
                                                        value={config.porcentaje_onp}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Descuento del empleado
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-3 mt-2">
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaShieldAlt className="me-1" />
                                                    Seguro de Salud (Microempresa)
                                                </Form.Label>
                                                <Form.Select
                                                    name="tipo_seguro_micro"
                                                    value={config.tipo_seguro_micro}
                                                    onChange={handleChange}
                                                    required
                                                    style={{ borderRadius: "10px" }}
                                                >
                                                    <option value="ESSALUD">ESSALUD (9%)</option>
                                                    <option value="SIS">SIS (Monto Fijo)</option>
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Elección del empleador para régimen Microempresa
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaDollarSign className="me-1" />
                                                    Costo SIS Microempresa
                                                </Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        S/
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="costo_sis"
                                                        value={config.costo_sis}
                                                        onChange={handleChange}
                                                        disabled={config.tipo_seguro_micro !== 'SIS'}
                                                        required={config.tipo_seguro_micro === 'SIS'}
                                                        style={{ borderRadius: "0 10px 10px 0" }}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Monto fijo mensual por trabajador
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* AFPs */}
                            {[
                                { key: 'integra', name: 'INTEGRA', color: '#007bff' },
                                { key: 'prima', name: 'PRIMA', color: '#28a745' },
                                { key: 'profuturo', name: 'PROFUTURO', color: '#dc3545' },
                                { key: 'habitat', name: 'HABITAT', color: '#ffc107' }
                            ].map((afp) => (
                                <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }} key={afp.key}>
                                    <Card.Header
                                        className="text-white py-3"
                                        style={{
                                            background: `linear-gradient(135deg, ${afp.color} 0%, ${afp.color}dd 100%)`,
                                            borderRadius: "15px 15px 0 0",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <FaShieldAlt className="me-2" />
                                            <h5 className="mb-0 fw-semibold">AFP {afp.name}</h5>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                        <FaPercent className="me-1" />
                                                        Fondo
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.0001"
                                                            name={`afp_${afp.key}.fondo`}
                                                            value={config[`afp_${afp.key}`].fondo}
                                                            onChange={handleChange}
                                                            required
                                                            style={{ borderRadius: "10px 0 0 10px" }}
                                                        />
                                                        <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                            %
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                        <FaPercent className="me-1" />
                                                        Seguro
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.0001"
                                                            name={`afp_${afp.key}.seguro`}
                                                            value={config[`afp_${afp.key}`].seguro}
                                                            onChange={handleChange}
                                                            required
                                                            style={{ borderRadius: "10px 0 0 10px" }}
                                                        />
                                                        <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                            %
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                        <FaPercent className="me-1" />
                                                        Comisión
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.0001"
                                                            name={`afp_${afp.key}.comision`}
                                                            value={config[`afp_${afp.key}`].comision}
                                                            onChange={handleChange}
                                                            required
                                                            style={{ borderRadius: "10px 0 0 10px" }}
                                                        />
                                                        <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                            %
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            ))}

                            {/* SCTR */}
                            <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="text-white py-3"
                                    style={{
                                        background: "linear-gradient(135deg, #6f42c1 0%, #6610f2 100%)",
                                        borderRadius: "15px 15px 0 0",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <BsShield className="me-2" />
                                        <h5 className="mb-0 fw-semibold">SCTR por Nivel de Riesgo</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {[1, 2, 3, 4, 5].map((nivel) => (
                                        <div key={nivel} className="mb-4">
                                            <div className="d-flex align-items-center mb-3">
                                                <Badge
                                                    style={{
                                                        background: `linear-gradient(135deg, #${nivel}${nivel}${nivel}333 0%, #${nivel}${nivel}${nivel}555 100%)`,
                                                        fontSize: "0.9rem",
                                                        padding: "8px 16px"
                                                    }}
                                                >
                                                    Riesgo Nivel {nivel}
                                                </Badge>
                                            </div>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                            <FaPercent className="me-1" />
                                                            SCTR Salud
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.0001"
                                                                name={`sctr.riesgo_${nivel}.salud`}
                                                                value={config.sctr[`riesgo_${nivel}`].salud}
                                                                onChange={handleChange}
                                                                required
                                                                style={{ borderRadius: "10px 0 0 10px" }}
                                                            />
                                                            <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                                %
                                                            </InputGroup.Text>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                            <FaPercent className="me-1" />
                                                            SCTR Pensión
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.0001"
                                                                name={`sctr.riesgo_${nivel}.pension`}
                                                                value={config.sctr[`riesgo_${nivel}`].pension}
                                                                onChange={handleChange}
                                                                required
                                                                style={{ borderRadius: "10px 0 0 10px" }}
                                                            />
                                                            <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                                %
                                                            </InputGroup.Text>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>

                            {/* Vida Ley */}
                            <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="text-white py-3"
                                    style={{
                                        background: "linear-gradient(135deg, #20c997 0%, #17a2b8 100%)",
                                        borderRadius: "15px 15px 0 0",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FaUserShield className="me-2" />
                                        <h5 className="mb-0 fw-semibold">Vida Ley por Rango Salarial</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaChartLine className="me-1" />
                                                    Hasta S/ 100,000
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="vida_ley.rango_1.tasa"
                                                        value={config.vida_ley.rango_1.tasa}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Tasa para remuneraciones hasta S/ 100,000
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaChartLine className="me-1" />
                                                    De S/ 100,000 a 200,000
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="vida_ley.rango_2.tasa"
                                                        value={config.vida_ley.rango_2.tasa}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Tasa para remuneraciones entre S/ 100,000 y S/ 200,000
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaChartLine className="me-1" />
                                                    Más de S/ 200,000
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="vida_ley.rango_3.tasa"
                                                        value={config.vida_ley.rango_3.tasa}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Tasa para remuneraciones mayores a S/ 200,000
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* SENATI */}
                            <Card className="mb-4 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Header
                                    className="text-white py-3"
                                    style={{
                                        background: "linear-gradient(135deg, #fd7e14 0%, #e83e8c 100%)",
                                        borderRadius: "15px 15px 0 0",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <FaGraduationCap className="me-2" />
                                        <h5 className="mb-0 fw-semibold">SENATI</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                                    <FaPercent className="me-1" />
                                                    Contribución SENATI
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.0001"
                                                        name="senati.tasa"
                                                        value={config.senati.tasa}
                                                        onChange={handleChange}
                                                        required
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>
                                                        %
                                                    </InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Tasa de contribución al SENATI (0.75% por defecto)
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Botón de guardar */}
                            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Body className="p-4 text-center">
                                    <Button
                                        type="submit"
                                        disabled={saving}
                                        size="lg"
                                        className="px-5 py-3"
                                        style={{
                                            borderRadius: "15px",
                                            background: saving 
                                                ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                                                : "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                                            border: "none",
                                            fontSize: "1.1rem",
                                            fontWeight: "600",
                                            minWidth: "200px"
                                        }}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                />
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-2" />
                                                Guardar Configuración
                                            </>
                                        )}
                                    </Button>
                                    {lastSaved && (
                                        <p className="text-muted mt-3 mb-0 small">
                                            <FaInfoCircle className="me-1" />
                                            Los cambios se aplicarán en los próximos cálculos de planilla
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default ConfiguracionPlanilla;